// Centro assistenza principale con categorie e ricerca
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Book, Ticket, Package, Shield, BarChart2, Settings, HelpCircle } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Search } from 'lucide-react'
import { useState } from 'react'
import api from '../../lib/api'

interface HelpCategory {
  id: string
  slug: string
  name: string
  description: string
  articleCount: number
  icon: string
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  tickets: <Ticket className="h-6 w-6" />,
  warehouse: <Package className="h-6 w-6" />,
  hse: <Shield className="h-6 w-6" />,
  analytics: <BarChart2 className="h-6 w-6" />,
  settings: <Settings className="h-6 w-6" />,
  general: <Book className="h-6 w-6" />,
}

export function HelpPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['help', 'categories'],
    queryFn: async () => {
      const res = await api.get<HelpCategory[]>('/help/categories')
      return res.data
    },
  })

  // /help/articles/popular non esiste nel backend — endpoint disabilitato
  // Gli articoli popolari vengono omessi dalla UI quando non disponibili
  const popular: Array<{ id: string; slug: string; title: string; views: number }> = []

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (search.trim()) {
      navigate(`/help/search?q=${encodeURIComponent(search)}`)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <PageHeader title="Centro assistenza" subtitle="Come possiamo aiutarti?" />

      {/* Ricerca */}
      <form onSubmit={handleSearch}>
        <Input
          placeholder="Cerca negli articoli..."
          leftIcon={<Search className="h-4 w-4" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="text-base py-3"
        />
      </form>

      {/* Categorie */}
      <div>
        <h2 className="mb-4 text-base font-semibold text-[var(--fc-foreground)]">Sfoglia per categoria</h2>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-lg bg-[var(--fc-gray-200)]" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className="flex flex-col items-center gap-2 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 text-center hover:border-[var(--fc-primary)] hover:shadow-[var(--fc-shadow)] transition-all"
                onClick={() => navigate(`/help/category/${cat.slug}`)}
                aria-label={cat.name}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--fc-primary)_10%,transparent)] text-[var(--fc-primary)]">
                  {CATEGORY_ICONS[cat.slug] ?? <HelpCircle className="h-6 w-6" />}
                </div>
                <div>
                  <p className="font-semibold text-sm text-[var(--fc-foreground)]">{cat.name}</p>
                  <p className="text-xs text-[var(--fc-foreground-muted)]">{cat.articleCount} articoli</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Articoli popolari */}
      {popular.length > 0 && (
        <div>
          <h2 className="mb-4 text-base font-semibold text-[var(--fc-foreground)]">Articoli più letti</h2>
          <div className="divide-y divide-[var(--fc-border)] rounded-lg border border-[var(--fc-border)]">
            {popular.map((article) => (
              <button
                key={article.id}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-[var(--fc-surface-raised)] transition-colors"
                onClick={() => navigate(`/help/article/${article.slug}`)}
              >
                <Book className="h-4 w-4 shrink-0 text-[var(--fc-foreground-muted)]" aria-hidden="true" />
                <span className="flex-1 text-sm text-[var(--fc-foreground)]">{article.title}</span>
                <span className="text-xs text-[var(--fc-foreground-subtle)]">{article.views} visualizzazioni</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
